/**
 * useStandort – lädt Standortdaten aus standortDaten.json per PLZ
 * Gibt Marktdaten + Score zurück, oder null wenn PLZ unbekannt
 *
 * Bundesland- und Marktdaten-Auflösung (12.10.2026, Dominiks Live-Test-Befund:
 * PLZ 99093/Erfurt bekam weder Bundesland noch Marktvergleich, obwohl andere
 * Erfurt-PLZ im Datensatz sind):
 * - Bundesland kommt jetzt fuer JEDE deutsche PLZ aus public/data/plzBundesland.json
 *   (nationale PLZ->Bundesland(+Ort)-Zuordnung, siehe dort meta.methodology). Ersetzt/
 *   integriert die bisherige stadtnamen-basierte cityBundesland.ts-Zuordnung und die
 *   7-Großstädte-Datei grossstaedteOhneMarktdaten.json (beide retired, 0 Abweichungen
 *   bei der Migration geprüft). Liegt eine PLZ in mehreren Bundesländern (seltene
 *   Grenz-PLZ, z. B. 21039 Hamburg/Schleswig-Holstein) -- bundesland bleibt null,
 *   bundeslandOptions nennt die Möglichkeiten, UI zeigt ein Dropdown.
 * - Marktdaten auf Stadtebene (Punkt 2): ist die PLZ NICHT in standortDaten.json, ihr
 *   Ort laut plzBundesland.json aber mit mindestens 3 ANDEREN PLZ dort vertreten, wird
 *   aus deren k/m/r der Median gebildet (cityFallback) -- Lage-Perzentil aus dem
 *   Median-k, wie fuer jede andere PLZ. Die 7 Großstädte haben naturgemäß 0 PLZ in
 *   standortDaten.json, sind also nie von diesem Fallback betroffen (siehe dort).
 */

import { STADTWERTE_TOP7, STADTWERTE_QUELLE } from "../core/stadtwerteTop7.js";
import { useState, useEffect, useMemo } from "react";
import { calcStandortScore, percentileOfK, type StandortScoreLabel } from "../lib/standortScore.js";
import type { LageInput } from "../core/score/dealScore";

export interface StandortInfo {
  stadt: string;
  kaufpreis_m2: number;
  miete_m2: number;
  rendite_brutto: number;
  score: number;
  scoreLabel: StandortScoreLabel;
  scoreColor: string;
  faktor: number;
  /** Perzentil des Kaufpreisniveaus ueber alle PLZ (0-100) -- fuer den Deal-Score (Lage-Komponente). */
  percentileK: number;
}

/** Marktdaten auf Stadtebene als Ausweichwert (Dominiks Vorgabe 12.10.2026, Punkt 2) --
 *  Median von k/m/r ueber alle ANDEREN PLZ derselben Stadt in standortDaten.json, nur
 *  wenn das mindestens 3 sind. Gleiche Form wie StandortInfo (percentileK aus dem
 *  Median-k), aber separat gehalten -- kein echter Treffer, nicht "info". */
export interface StandortCityFallback {
  stadt: string;
  kaufpreis_m2: number;
  miete_m2: number;
  rendite_brutto: number;
  percentileK: number;
  /** Anzahl der PLZ, aus denen der Median gebildet wurde (fuer Tests/Transparenz). 0 bei Stadtwerten. */
  plzCount: number;
  /** Gesetzt bei recherchierten Stadtwerten (Top-7, src/core/stadtwerteTop7.ts) statt PLZ-Median. */
  quelle?: string;
}

type PlzEntry = { s: string; k: number; m: number; r: number };
type PlzBundeslandEntry = { ort: string; bundesland: string | string[] };

let cache: Record<string, PlzEntry> | null = null;
let plzBundeslandCache: Record<string, PlzBundeslandEntry> | null = null;

// Phase-4 Runde 2a (Seitenleiste "Meine Deals"): exportiert, damit AppShell.tsx denselben
// modulweiten Cache fuer eine Standort-Aufloesung je Sidebar-Deal nutzen kann, OHNE den
// Hook useStandort() aufzurufen -- der ist an EINE PLZ pro Komponenteninstanz gebunden,
// in einer Liste mehrerer Deals mit unterschiedlichen PLZ aber nicht in einer Schleife
// aufrufbar (Rules of Hooks). resolveStandort() (bereits exportiert) ist die reine
// Auflösungslogik dafuer.
// Ladezeit (18.10.2026): laufende Requests werden geteilt -- parallele Aufrufer (Objektseite,
// Seitenleiste, Vorab-Laden) lösen nur EINEN Download aus statt mehrerer.
let dataInflight: Promise<Record<string, PlzEntry>> | null = null;
let plzBundeslandInflight: Promise<Record<string, PlzBundeslandEntry>> | null = null;

export async function loadData(): Promise<Record<string, PlzEntry>> {
  if (cache) return cache;
  if (!dataInflight) {
    dataInflight = fetch("/data/standortDaten.json")
      .then((res) => res.json())
      .then((json) => { cache = json.plz; return cache!; })
      .finally(() => { dataInflight = null; });
  }
  return dataInflight;
}

export async function loadPlzBundesland(): Promise<Record<string, PlzBundeslandEntry>> {
  if (plzBundeslandCache) return plzBundeslandCache;
  if (!plzBundeslandInflight) {
    plzBundeslandInflight = fetch("/data/plzBundesland.json")
      .then((res) => res.json())
      .then((json) => { plzBundeslandCache = json.plz; return plzBundeslandCache!; })
      .finally(() => { plzBundeslandInflight = null; });
  }
  return plzBundeslandInflight;
}

/** Marktdaten schon laden, bevor die PLZ bekannt ist (z. B. während das Objekt vom Server kommt). */
export function vorabLadenStandortDaten(): void {
  loadData().catch(() => {});
  loadPlzBundesland().catch(() => {});
}

function sortedKs(data: Record<string, PlzEntry>): number[] {
  return Object.values(data).map((e) => e.k).sort((a, b) => a - b);
}

/** Gruppiert standortDaten.json nach Stadt (s) -- fuer den Stadtebene-Fallback. Bewusst
 *  ohne modulweiten Cache (anders als zuvor): resolveStandort() soll mit beliebigen
 *  data-Objekten direkt testbar sein (siehe tests/plz-bundesland.test.mjs), ein globaler
 *  Cache wuerde ueber mehrere Testfaelle mit unterschiedlichen Mock-Daten hinweg veraltete
 *  Ergebnisse liefern. ~1.200 Eintraege, pro PLZ-Aenderung einmal -- performanceunkritisch. */
function groupByStadt(data: Record<string, PlzEntry>): Map<string, PlzEntry[]> {
  const byStadt = new Map<string, PlzEntry[]>();
  for (const entry of Object.values(data)) {
    if (!byStadt.has(entry.s)) byStadt.set(entry.s, []);
    byStadt.get(entry.s)!.push(entry);
  }
  return byStadt;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Mindestanzahl ANDERER PLZ derselben Stadt in standortDaten.json, ab der der
 *  Stadtebene-Fallback greift (Dominiks Vorgabe 12.10.2026, Punkt 2). */
export const CITY_FALLBACK_MIN_PLZ = 3;

export interface ResolvedStandort {
  info: StandortInfo | null;
  notFound: boolean;
  bundesland: string | null;
  bundeslandOptions: string[] | null;
  cityFallback: StandortCityFallback | null;
  ort: string | null;
}

/**
 * Reine Auflösungslogik fuer eine PLZ -- ausgelagert aus dem Hook-Effect, damit Punkt 2
 * (Marktdaten auf Stadtebene, score-relevant) ohne React/fetch-Mocking direkt getestet
 * werden kann (siehe tests/plz-bundesland.test.mjs). data/plzBundesland sind die bereits
 * geladenen JSON-Inhalte (standortDaten.json.plz bzw. plzBundesland.json.plz).
 */
export function resolveStandort(
  plz: string,
  data: Record<string, PlzEntry>,
  plzBundesland: Record<string, PlzBundeslandEntry>
): ResolvedStandort {
  const d = data[plz];
  const bl = plzBundesland[plz];
  const bundesland = Array.isArray(bl?.bundesland) ? null : bl?.bundesland ?? null;
  const bundeslandOptions = Array.isArray(bl?.bundesland) ? bl.bundesland : null;
  const ort = bl?.ort ?? null;

  if (d) {
    const { score, label, color } = calcStandortScore(d.k, d.m, d.r);
    return {
      info: {
        stadt: d.s,
        kaufpreis_m2: d.k,
        miete_m2: d.m,
        rendite_brutto: d.r,
        score,
        scoreLabel: label,
        scoreColor: color,
        faktor: Math.round(d.k / (d.m * 12) * 10) / 10,
        percentileK: percentileOfK(sortedKs(data), d.k),
      },
      notFound: false,
      bundesland,
      bundeslandOptions,
      cityFallback: null,
      ort,
    };
  }

  // Marktdaten auf Stadtebene (Punkt 2): nur, wenn die PLZ einem Ort zugeordnet ist UND
  // dieser Ort mit mindestens 3 ANDEREN PLZ in standortDaten.json steht. Die 7 Großstädte
  // (0 PLZ dort) erreichen diese Schwelle nie, bleiben unveraendert ohne cityFallback.
  const plzInStadt = ort ? groupByStadt(data).get(ort) ?? [] : [];
  let cityFallback: StandortCityFallback | null = null;
  if (ort && plzInStadt.length >= CITY_FALLBACK_MIN_PLZ) {
    const medianK = median(plzInStadt.map((e) => e.k));
    const medianM = median(plzInStadt.map((e) => e.m));
    const medianR = median(plzInStadt.map((e) => e.r));
    cityFallback = {
      stadt: ort,
      kaufpreis_m2: medianK,
      miete_m2: medianM,
      rendite_brutto: medianR,
      percentileK: percentileOfK(sortedKs(data), medianK),
      plzCount: plzInStadt.length,
    };
  } else if (ort && STADTWERTE_TOP7[ort]) {
    // Top-7-Großstädte (18.10.2026): recherchierte Stadtwerte statt „keine Marktdaten“.
    const w = STADTWERTE_TOP7[ort];
    cityFallback = {
      stadt: ort,
      kaufpreis_m2: w.k,
      miete_m2: w.m,
      rendite_brutto: w.r,
      percentileK: percentileOfK(sortedKs(data), w.k),
      plzCount: 0,
      quelle: STADTWERTE_QUELLE,
    };
  }

  return { info: null, notFound: true, bundesland, bundeslandOptions, cityFallback, ort };
}

export function useStandort(plz: string) {
  const [info, setInfo] = useState<StandortInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [bundesland, setBundesland] = useState<string | null>(null);
  const [bundeslandOptions, setBundeslandOptions] = useState<string[] | null>(null);
  const [cityFallback, setCityFallback] = useState<StandortCityFallback | null>(null);
  /** Ort laut plzBundesland.json, UNABHAENGIG davon, ob cityFallback greift (Schwelle
   *  nicht erreicht oder 0 PLZ, z. B. die 7 Großstädte) -- fuer die "Für {Ort} haben wir
   *  aktuell noch keine Marktdaten"-Anzeige, die dann weiterhin den Stadtnamen nennt. */
  const [ort, setOrt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (plz.length !== 5) {
      setInfo(null);
      setNotFound(false);
      setBundesland(null);
      setBundeslandOptions(null);
      setCityFallback(null);
      setOrt(null);
      return;
    }

    setLoading(true);
    Promise.all([loadData(), loadPlzBundesland()]).then(([data, plzBundesland]) => {
      const resolved = resolveStandort(plz, data, plzBundesland);
      setInfo(resolved.info);
      setNotFound(resolved.notFound);
      setBundesland(resolved.bundesland);
      setBundeslandOptions(resolved.bundeslandOptions);
      setCityFallback(resolved.cityFallback);
      setOrt(resolved.ort);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [plz]);

  // Bugfix 15.10.2026 (per echtem DOM-Test gefunden, tests/e2e/deal-akte.logged-in.spec.ts):
  // ohne useMemo liefert dieser Hook bei JEDEM Render ein neues Objekt-Literal -- selbst
  // wenn sich info/notFound/... nicht geaendert haben. DealDetail.tsx und MFHCheck.tsx
  // (ebenso die anderen 4 Analyzer) reichen das Ergebnis per lageInputFromStandort() in
  // ein useMemo([..., standort, ...]) weiter, dessen Resultat wiederum in einem useEffect
  // landet, der setState aufruft -- die neue Referenz bei jedem Render liess diesen Effect
  // auf JEDEM Render erneut feuern -> "Maximum update depth exceeded" (Endlosschleife,
  // DealDetail haengt sichtbar fest; auf den Analyzer-Routen vermutlich derselbe
  // Mechanismus, siehe StandortPanel.tsx/MFHCheck.tsx -- nicht nur ein DealDetail-Bug).
  return useMemo(
    () => ({ info, notFound, loading, bundesland, bundeslandOptions, cityFallback, ort }),
    [info, notFound, loading, bundesland, bundeslandOptions, cityFallback, ort]
  );
}

/**
 * LageInput fuer den Deal-Score aus einem useStandort()-Ergebnis -- EINE Stelle statt der
 * bisherigen, 6-fach duplizierten Inline-Fallunterscheidung (Check.tsx + alle 5 Analyzer),
 * damit Punkt 2 (Marktdaten auf Stadtebene) ueberall konsistent in die Score-Berechnung
 * einfliesst. Reihenfolge: echter Treffer > Stadtebene-Median (Punkt 2) > PLZ unbekannt >
 * PLZ (noch) leer. Score-Logik selbst (dealScore.ts) bleibt unveraendert -- der
 * Stadtebene-Median liefert nur denselben {kind:"ok", r, percentileK}-Input, den ein
 * echter Treffer auch liefern wuerde.
 */
export function lageInputFromStandort(
  standort: Pick<ReturnType<typeof useStandort>, "info" | "notFound" | "cityFallback">,
  plzComplete: boolean
): LageInput {
  if (!plzComplete) return { kind: "ohne_plz" };
  if (standort.info) return { kind: "ok", r: standort.info.rendite_brutto, percentileK: standort.info.percentileK };
  if (standort.cityFallback) return { kind: "ok", r: standort.cityFallback.rendite_brutto, percentileK: standort.cityFallback.percentileK };
  if (standort.notFound) return { kind: "plz_unbekannt" };
  return { kind: "ohne_plz" }; // Standortdaten noch am Laden
}

/** Die 7 groessten deutschen Staedte (Dominiks Vorgabe, Druebeck-Pruefung 18.10.2026) --
 *  Schreibweise exakt wie `ort` in public/data/plzBundesland.json (Frankfurt dort als
 *  "Frankfurt am Main"). Frueher eine eigene, seit 12.10.2026 retired Datei
 *  (grossstaedteOhneMarktdaten.json, siehe Dateikommentar oben) -- die Migration auf
 *  plzBundesland.json hat diese Teilmenge nicht mitgenommen, wodurch grossstadtOhneMarktdaten
 *  faelschlich fuer JEDE PLZ ohne Marktdaten gesetzt wurde, nicht nur die 7 Grossstaedte
 *  (Nachtest-Befund: PLZ 38871/Druebeck, ein winziges Dorf, zeigte denselben "fuer <Stadt>
 *  haben wir aktuell noch keine Marktdaten"-Satz wie Berlin/Hamburg/...). */
export const TOP_7_GROSSSTAEDTE = ["Berlin", "Hamburg", "München", "Köln", "Frankfurt am Main", "Stuttgart", "Düsseldorf"] as const;

/** Grossstadt-ohne-Marktdaten-Fall (Dominiks Vorgabe, Druebeck-Pruefung 18.10.2026): nur
 *  gesetzt, wenn `ort` tatsaechlich einer der TOP_7_GROSSSTAEDTE ist -- siehe Dateikommentar
 *  bei TOP_7_GROSSSTAEDTE fuer den Befund, der diesen Fix ausgeloest hat. EINE Stelle statt
 *  der bisherigen, 7-fach duplizierten Inline-Pruefung (5 Analyzer + 2x DealDetail.tsx), die
 *  `standort.ort` ungefiltert durchreichte. */
export function grossstadtOhneMarktdatenFromStandort(
  standort: Pick<ReturnType<typeof useStandort>, "notFound" | "cityFallback" | "ort">
): string | undefined {
  if (!standort.notFound || standort.cityFallback) return undefined;
  const ort = standort.ort;
  return ort && (TOP_7_GROSSSTAEDTE as readonly string[]).includes(ort) ? ort : undefined;
}

/** Kleinere-Gemeinde-ohne-Marktdaten-Fall (Dominiks Vorgabe, Druebeck-Pruefung 18.10.2026):
 *  spiegelbildlich zu grossstadtOhneMarktdatenFromStandort() -- true genau dann, wenn weder
 *  ein echter Treffer noch ein Stadtebene-Median noch einer der 7 Grossstaedte vorliegt
 *  (also PLZ UND Ort beide ohne jede Marktdaten-Grundlage, z. B. PLZ 38871/Druebeck). */
export function istKleinereGemeindeOhneMarktdaten(
  standort: Pick<ReturnType<typeof useStandort>, "notFound" | "cityFallback" | "ort">
): boolean {
  return standort.notFound && !standort.cityFallback && grossstadtOhneMarktdatenFromStandort(standort) === undefined;
}
